import { inputStyle } from "./styles";
import { tokens } from "../../Common/designTokens";

/**
 * Input styling for the List RFQ bond grid.
 *
 * In plain terms: the same boxes the rest of Order Entry uses, but shorter, so a list of
 * ten bonds still fits on screen.
 *
 * Built by extending the shared {@link inputStyle} rather than replacing it, so the border,
 * hover, focus, disabled and error treatments stay identical to every other field in the
 * modal. Two things are overridden, both because the grid has no floating labels - the
 * column header carries the label instead:
 *
 *   - paddingTop 10px, which inputStyle reserves for the floating label, is removed
 *   - the input itself is given a fixed compact height
 *
 * Values are 15px. The heights below are sized to that - 15px text needs roughly 18px of line
 * box, so the 18px the input used to have would have clipped descenders. Raise the font again
 * and these have to move with it.
 *
 * This matters for space: OrderEntry's modal is 800px wide (modalStyle), so a row of
 * full-height labelled fields would not fit without horizontal scrolling.
 */
export const compactInputStyle = {
  ...inputStyle,
  "& .MuiFilledInput-root": {
    ...inputStyle["& .MuiFilledInput-root"],
    paddingTop: 0,
    borderRadius: "6px",
    minHeight: "34px",
  },
  "& .MuiFilledInput-input": {
    padding: "7px 8px",
    fontSize: "15px",
    height: "20px",
  },
  "& .MuiSelect-filled": {
    ...inputStyle["& .MuiSelect-filled"],
    paddingTop: "7px",
    paddingBottom: "7px",
    // .MuiFilledInput-input above sets `padding: "7px 8px"` on the same node, which - being a
    // shorthand - leaves only 8px clear on the right unless something more specific overrides
    // it. MUI's own dropdown arrow needs about 24px to sit in without touching the text; 8px
    // was not enough, and a long value like "PROVIDE" (the widest of Side/Price Type/Strategy)
    // ran directly under the arrow. This is a longhand after that shorthand, so it wins for
    // this one side without disturbing the top/bottom/left padding above.
    paddingRight: "26px",
    fontSize: "15px",
    minHeight: "20px",
  },
  "& .MuiAutocomplete-inputRoot": {
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
    minHeight: "32px",
  },
  "& .MuiChip-root": {
    height: "20px",
    fontSize: "11px",
  },
};

/** Header cell text - small, quiet, and the same on every column. */
/**
 * Column headings for the bonds grid, matched to the Market Data table's headings.
 *
 * Every value here is what a Market Data header cell actually renders with:
 *   fontSize 14px + fontWeight 600 + whiteSpace nowrap   MarketDataInfoPanel headerCellStyle
 *   color headerFontColour                               theme.js MuiTableCell.head (~line 526),
 *                                                        which !important-beats the fontColour85
 *                                                        that panel asks for, so this is the ink
 *                                                        that reaches the screen
 *
 * No letterSpacing: the 0.02em here before was this grid's own, and Market Data sets none.
 * The labels all still fit their columns at 14px - the tightest is "Stipulation" at roughly
 * 76px in a 104px column.
 */
export const gridHeaderTextStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: tokens.colors.headerFontColour,
  whiteSpace: "nowrap",
};

/**
 * The "+ Add CUSIP" affordance: a dashed outline, so it reads as "there is room for more"
 * rather than as another action button competing with Send and Clear All.
 */
export const addBondButtonStyle = {
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 600,
  py: 0.25,
  px: 1.25,
  borderRadius: "6px",
  border: `1px dashed ${tokens.palette.primary}`,
  color: tokens.palette.primary,
  "&:hover": {
    border: `1px dashed ${tokens.palette.primary}`,
    backgroundColor: tokens.colors.bodyHoverBackgroundColor,
  },
};

/**
 * How far the Dealers preview text must stop short of the right-hand edge, in pixels.
 *
 * In plain terms: the space reserved for the two little buttons at the end of the Dealers
 * box, so the dealer names never run underneath them.
 *
 * MUI's Autocomplete puts two icon buttons there - the clear cross and the dropdown arrow.
 * The arrow is always shown; the CROSS APPEARS ONLY ON HOVER, which is why this was wrong
 * and looked fine at rest: the old 34px cleared the arrow alone, so the moment the trader
 * moved the mouse over the cell a cross dropped on top of the last letters.
 *
 * 52 = 4px offset from the edge + two 24px buttons (a 20px icon with 2px of padding each
 * side, set by dealerInputStyle below). Those three numbers have to move together, which is
 * why they live next to each other.
 */
export const DEALER_PREVIEW_RIGHT_PX = 52;

/**
 * The Dealers cell input - compactInputStyle plus the sizing of those two icon buttons.
 *
 * Taken from the single-bond Dealer field (ActualOrderTabOrderPrimaryFields), which already
 * pins the end adornment and both indicators for the same reason. The padding here is 2px
 * rather than that field's 4px because this one sits in a 190px grid column rather than a
 * quarter-width form field, and every pixel it gives back is a pixel of dealer name.
 */
export const dealerInputStyle = {
  ...compactInputStyle,
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
    right: "4px",
    marginLeft: 0,
    display: "flex",
    gap: 0,
  },
  "& .MuiAutocomplete-clearIndicator": {
    padding: "2px",
    marginRight: 0,
  },
  "& .MuiAutocomplete-popupIndicator": {
    padding: "2px",
  },
  "& .MuiAutocomplete-inputRoot": {
    ...compactInputStyle["& .MuiAutocomplete-inputRoot"],
    // One line only. Without this the input wraps once the selection is long, and the cell
    // grows taller than every other cell in the row.
    flexWrap: "nowrap",
  },
};
